import React, { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';

import FormHeader from 'prequalificationV2/components/FormHeader';
import {
  LoadingType,
  PreQualificationStep,
  setLoadingState,
  setPreQualificationStep,
} from 'prequalificationV2/actions';
import { setBorrower } from 'prequalificationV2/actions/borrower';
import { setCoBorrower } from 'prequalificationV2/actions/coBorrower';
import { setData } from 'prequalificationV2/actions/data';
import { setPrequalificationPortal } from 'prequalificationV2/actions/prequalificationPortal';
import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import Input from 'components/Inputs/Input';
import NumberInput from 'components/Inputs/NumberInput';
import Loader from 'components/Loader';
import Select from 'components/Select';
import { NOT_LATIN_ALPHABET_REG_EX } from 'constants/constants';
import { ApplicationType } from 'enums/ApplicationType';
import { ContractorNameSource } from 'enums/ApplicationsVariables';
import { LoanInformationVariables } from 'prequalificationV2/enums/LoanInformationVariables';
import { LoanPurpose } from 'enums/LoanPurpose';
import { SwimmingPoolType } from 'enums/SwimmingPoolType';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { OptionType } from 'prequalificationV2/utils/inputSelect';
import {
  getErrorMessageForRequiredFields,
  MONETARY_PREFIX,
  removeNonDigits,
} from 'prequalificationV2/utils/stringUtils';
import { isOnlyLatinAlphabet } from 'prequalificationV2/utils/validationUtils';
import { ButtonType } from 'enums/ButtonType';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import { setApplication } from 'prequalificationV2/actions/application';
import { preQualificationApi } from 'prequalificationV2/api/PreQualification/PreQualificationApi';
import {
  mapBorrowerToUnderwritingBorrowerDto,
  mapCoBorrowerToUnderwritingCoBorrowerDto,
  mapUnderwritingGeneralDto,
} from 'prequalificationV2/api/PreQualification/preQualificationApi.utils';
import { getConceptualDeviceId } from 'prequalificationV2/routes/routes.utils';
import { checkUserShouldBeRedirectToFinMktPortal } from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/loanInformation.utils';
import {
  MAX_LOAN_FOR_PORTAL,
  MIN_LOAN_FOR_PORTAL,
  MINIMUM_LOAN_AMOUNT,
} from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/loanInformation.constants';
import styles from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/LoanInformation.module.scss';
import LoanAmount from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/LoanAmount/LoanAmount';
import { MAP_PRODUCT_NAME_TO_LOAN_AMOUNT_TYPE } from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/LoanAmount/loanAmount.constants';
import { LoanAmountType } from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/LoanAmount/loanAmount.enums';
import { marketingApi } from 'api/MarketingApi';
import { PrequalificationVersion } from 'enums/ApplicationsVariables';
import { ConceptualHqEvent } from 'enums/ConceptualHqEvent';
import AgreementPopup from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/AgreementPopup/AgreementPopup';
import AcceptTermsOfUse from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/AgreementPopup/AcceptTermsOfUse/AcceptTermsOfUse';
import TranscriptAuthorization from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/AgreementPopup/TranscriptAuthorization/TranscriptAuthorization';
import { AgreementType } from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/AgreementPopup/agreementPopup.enums';
import { Checkbox, CheckboxType } from 'components/Checkbox';
import { getMessageForRequiredFields } from 'utils/validationUtils';
import { IntermediaryRefer } from 'prequalificationV2/enums/IntermediaryRefer';
import { useNavigateAfterUnderwriting } from 'prequalificationV2/hooks/useNavigateAfterUnderwriting';

const getSelectOptions = (selectOptionsType: { [key: string]: string }): OptionType[] => {
  const selectOptions = [];

  for (let optionKey in selectOptionsType) {
    selectOptions.push({
      label: selectOptionsType[optionKey],
      value: selectOptionsType[optionKey],
    });
  }

  return selectOptions;
};

const LoanInformation = () => {
  const dispatch = useAppDispatch();
  const { handleUnderwritingResult } = useNavigateAfterUnderwriting();
  const { application, borrower, coBorrower, data, proveErrorInfo } = useAppSelector((state) => state);
  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const {
    register,
    unregister,
    formState: { errors },
    setValue,
    trigger,
    watch,
    control,
    clearErrors,
    handleSubmit,
  } = useForm({
    defaultValues: {
      [LoanInformationVariables.LoanPurpose]: application[LoanInformationVariables.LoanPurpose],
      [LoanInformationVariables.SwimmingPoolType]: application[LoanInformationVariables.SwimmingPoolType],
      [LoanInformationVariables.LoanAmount]: +(application[LoanInformationVariables.LoanAmount] || 0),
      [LoanInformationVariables.OtherHomeImprovementType]:
        application[LoanInformationVariables.OtherHomeImprovementType],
      borrowerAnnualIncome: borrower.annualIncome ? +borrower.annualIncome : undefined,
      coborrowerAnnualIncome: coBorrower?.annualIncome ? +coBorrower?.annualIncome : undefined,
      loanType: application[LoanInformationVariables.LoanType],
      agreeToDocuments: application.isDocumentAgreed,
    },
  });
  const isJoint = application[LoanInformationVariables.LoanType] === ApplicationType.Joint;
  const [isAgreementPopupOpen, setIsAgreementPopupOpen] = useState(false);
  const [agreementType, setAgreementType] = useState<AgreementType | null>(null);

  const watcher = watch();
  const loanAmountType = application?.productName
    ? MAP_PRODUCT_NAME_TO_LOAN_AMOUNT_TYPE[application.productName]
    : MAP_PRODUCT_NAME_TO_LOAN_AMOUNT_TYPE.default;

  useEffect(() => {
    register(LoanInformationVariables.LoanPurpose, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.LoanPurpose),
    });
    register(LoanInformationVariables.LoanAmount, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.LoanAmount),
      validate: (value) => {
        const loanAmount = +removeNonDigits(value);

        if (loanAmountType === LoanAmountType.HomeImprovement && loanAmount < MIN_LOAN_FOR_PORTAL) {
          return 'Loan Amount must be greater or equal to $15,000';
        }

        if (loanAmountType === LoanAmountType.PointOfSale && loanAmount >= MIN_LOAN_FOR_PORTAL) {
          return 'Loan Amount must be $14,999 or less';
        }

        if (loanAmount > MAX_LOAN_FOR_PORTAL) {
          return 'Loan Amount must be less or equal to $500,000';
        }

        if (loanAmount < MINIMUM_LOAN_AMOUNT) {
          return 'Loan Amount must be greater or equal to $1,000';
        }
      },
    });
    register('borrowerAnnualIncome', {
      required: 'Income is required',
    });

    if (isJoint) {
      register('coborrowerAnnualIncome', {
        required: 'Income is required',
      });
    }
  }, [register, unregister, watcher]);

  useEffect(() => {
    if (watcher[LoanInformationVariables.LoanPurpose] === LoanPurpose.OtherHomeImprovement) {
      register(LoanInformationVariables.OtherHomeImprovementType, {
        required: getErrorMessageForRequiredFields(LoanInformationVariables.OtherHomeImprovementType),
        validate: (value) =>
          isOnlyLatinAlphabet(value) || 'Please enter a valid Type. Type can only contain Latin letters',
      });
    }

    if (watcher[LoanInformationVariables.LoanPurpose] === LoanPurpose.SwimmingPool) {
      register(LoanInformationVariables.SwimmingPoolType, {
        required: getErrorMessageForRequiredFields(LoanInformationVariables.SwimmingPoolType),
      });
    }

    return () => {
      unregister(LoanInformationVariables.OtherHomeImprovementType);
      unregister(LoanInformationVariables.SwimmingPoolType);
    };
  }, [register, unregister, watcher[LoanInformationVariables.LoanPurpose]]);

  useEffect(() => {
    const track = async () => {
      const deviceId = await getConceptualDeviceId();

      if (!deviceId) {
        return;
      }

      marketingApi
        .trackConceptualHqEvent({
          event: ConceptualHqEvent.Financial,
          prequalificationVersion: PrequalificationVersion.Second,
          borrowerId: borrower.borrowerId,
          deviceId: deviceId,
        })
        .catch(() => {});
    };

    track();
  }, []);

  const validateLoanForm = handleSubmit(async () => {
    try {
      const result = await trigger();

      if (result) {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

        const loanAmount = +removeNonDigits(watcher[LoanInformationVariables.LoanAmount]);

        if (checkUserShouldBeRedirectToFinMktPortal(loanAmount)) {
          window.location.href = process.env.REACT_APP_LOW_LOAN_AMOUNT_REDIRECT_URL!;

          return;
        }

        const updatableLoanVariables = {
          [LoanInformationVariables.LoanPurpose]: watcher[LoanInformationVariables.LoanPurpose] as LoanPurpose,
          [LoanInformationVariables.OtherHomeImprovementType]:
            watcher[LoanInformationVariables.OtherHomeImprovementType],
          [LoanInformationVariables.SwimmingPoolType]: watcher[
            LoanInformationVariables.SwimmingPoolType
          ] as SwimmingPoolType,
          [LoanInformationVariables.LoanAmount]: +removeNonDigits(watcher[LoanInformationVariables.LoanAmount]),
          isDocumentAgreed: watcher.agreeToDocuments,
        };

        dispatch(setApplication(updatableLoanVariables));

        dispatch(
          setBorrower({
            annualIncome: +removeNonDigits(watcher.borrowerAnnualIncome),
          }),
        );

        if (isJoint) {
          dispatch(
            setCoBorrower({
              annualIncome: +removeNonDigits(watcher.coborrowerAnnualIncome),
            }),
          );
        }

        const underwriting = await preQualificationApi.runUnderwriting({
          borrowerData: mapBorrowerToUnderwritingBorrowerDto({
            ...borrower,
            annualIncome: +removeNonDigits(watcher.borrowerAnnualIncome),
          }),
          coborrowerData: isJoint
            ? mapCoBorrowerToUnderwritingCoBorrowerDto({
                ...coBorrower,
                annualIncome: +removeNonDigits(watcher.coborrowerAnnualIncome),
              })
            : null,
          generalData: mapUnderwritingGeneralDto({ ...application, ...updatableLoanVariables }, data, proveErrorInfo),
          ...(data.refer?.toLowerCase() === IntermediaryRefer.JobTread && {
            jobTreadData: {
              contractorUuid: data.jobTreadContractorUuid,
              documentId: data.jobTreadDocumentId,
              contractorName: data.jobTreadContractorName,
              loanAmount: data.jobTreadLoanAmount,
            },
          }),
        });

        // The contractor was captured for this application only. Clearing it here keeps the next application
        // started in the same browser session from inheriting a promo link it never arrived through.
        dispatch(
          setData({
            contractorName: '',
            contractorId: '',
            salesPerson: '',
            salespersonData: {},
            promoCode: '',
            contractorAutofillUsed: ContractorNameSource.Empty,
          }),
        );
        dispatch(setPrequalificationPortal({ isContractorNameInputDisabled: false }));

        await handleUnderwritingResult(underwriting);
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
    }
  });

  const handleBack = () => {
    if (watcher.loanType !== ApplicationType.Individual) {
      dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerInfo));
    } else {
      dispatch(setPreQualificationStep(PreQualificationStep.BorrowerInfo));
    }
  };

  const setLoanAmountValue = (value: number) => {
    clearErrors(LoanInformationVariables.LoanAmount);
    setValue(LoanInformationVariables.LoanAmount, value);
    trigger(LoanInformationVariables.LoanAmount);
  };

  const openAgreementPopup = (type: AgreementType) => {
    setAgreementType(type);
    setIsAgreementPopupOpen(true);
  };

  const closeAgreementPopup = () => {
    setIsAgreementPopupOpen(false);
    setAgreementType(null);
  };

  const getAgreementDocument = () => {
    switch (agreementType) {
      case AgreementType.TranscriptAuthorization:
        return <TranscriptAuthorization />;
      case AgreementType.AcceptTermsOfUse:
        return <AcceptTermsOfUse />;
      default:
        return null;
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className={styles.infoContainer}>
            <div className={styles.infoContent}>Inquiry Does NOT Affect Your Credit Scores</div>
          </div>
          <FormHeader title="Financial" percent={50} step={3} totalSteps={4} />
          <FormBody formBodyClassName={styles.formBody}>
            <LoanAmount
              value={watcher[LoanInformationVariables.LoanAmount]}
              setLoanAmountValue={setLoanAmountValue}
              errorMessage={errors[LoanInformationVariables.LoanAmount]?.message}
              type={loanAmountType}
            />
            <div className={styles.rowContainer}>
              <Select
                useFormControl
                required
                control={control}
                onChange={(event: any) => {
                  setValue(LoanInformationVariables.LoanPurpose, event.value as LoanPurpose);
                  trigger(LoanInformationVariables.LoanPurpose);
                }}
                name={LoanInformationVariables.LoanPurpose}
                label={LoanInformationVariables.LoanPurpose}
                options={getSelectOptions(LoanPurpose)}
                value={getSelectOptions(LoanPurpose).find(
                  (option: OptionType) => watcher[LoanInformationVariables.LoanPurpose!] === option.value,
                )}
                placeholder="Select Your Loan Purpose"
                inputContainerClassName={styles.formInput}
                errorMessage={errors[LoanInformationVariables.LoanPurpose]?.message}
              />
              {watcher[LoanInformationVariables.LoanPurpose] === LoanPurpose.SwimmingPool && (
                <Select
                  useFormControl
                  required
                  control={control}
                  onChange={(event: any) => {
                    setValue(LoanInformationVariables.SwimmingPoolType, event.value as SwimmingPoolType);
                    trigger(LoanInformationVariables.SwimmingPoolType);
                  }}
                  name={LoanInformationVariables.SwimmingPoolType}
                  label={LoanInformationVariables.SwimmingPoolType}
                  options={getSelectOptions(SwimmingPoolType)}
                  value={getSelectOptions(SwimmingPoolType).find(
                    (option: OptionType) => watcher[LoanInformationVariables.SwimmingPoolType!] === option.value,
                  )}
                  inputContainerClassName={styles.formInput}
                  errorMessage={errors[LoanInformationVariables.SwimmingPoolType]?.message}
                />
              )}
              {watcher[LoanInformationVariables.LoanPurpose] === LoanPurpose.OtherHomeImprovement && (
                <Input
                  className={styles.formInput}
                  errorMessage={errors[LoanInformationVariables.OtherHomeImprovementType]?.message}
                  name={LoanInformationVariables.OtherHomeImprovementType}
                  label={LoanInformationVariables.OtherHomeImprovementType}
                  onChange={(event) => {
                    let filteredLatinAlphabet = event.target.value.replace(NOT_LATIN_ALPHABET_REG_EX, '');

                    setValue(LoanInformationVariables.OtherHomeImprovementType, filteredLatinAlphabet);
                    trigger(LoanInformationVariables.OtherHomeImprovementType);
                  }}
                  value={watcher[LoanInformationVariables.OtherHomeImprovementType]}
                />
              )}
            </div>
            <p className={styles.blockTitle}>Income</p>
            <div className={styles.rowContainer}>
              <NumberInput
                label={`Borrower's Annual Income`}
                prefix={MONETARY_PREFIX}
                placeholder={MONETARY_PREFIX}
                thousandSeparator
                className={styles.incomeInput}
                name={'borrowerAnnualIncome'}
                onChange={(event: any) => {
                  setValue('borrowerAnnualIncome', event.target.value);
                  trigger('borrowerAnnualIncome');
                }}
                errorMessage={errors.borrowerAnnualIncome?.message}
                value={watcher.borrowerAnnualIncome || ''}
                allowNegative={false}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                  }
                }}
              />
              {isJoint && (
                <NumberInput
                  label={`Coborrower's Annual Income`}
                  prefix={MONETARY_PREFIX}
                  placeholder={MONETARY_PREFIX}
                  thousandSeparator
                  className={styles.incomeInput}
                  name={'coborrowerAnnualIncome'}
                  onChange={(event: any) => {
                    setValue('coborrowerAnnualIncome', event.target.value);
                    trigger('coborrowerAnnualIncome');
                  }}
                  errorMessage={errors.coborrowerAnnualIncome?.message}
                  value={watcher.coborrowerAnnualIncome || ''}
                  allowNegative={false}
                  onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                    }
                  }}
                />
              )}
            </div>
            <Controller
              name="agreeToDocuments"
              control={control}
              rules={{
                required: getMessageForRequiredFields(),
                validate: (value) => value === true || getMessageForRequiredFields(),
              }}
              render={() => (
                <div className={styles.agreementContainer}>
                  <div className={styles.checkboxContainer}>
                    <div className={styles.checkboxWrapper}>
                      <Checkbox
                        type={CheckboxType.Alternative}
                        name="agreeToDocuments"
                        isChecked={!!watcher.agreeToDocuments}
                        onChange={() => {
                          setValue('agreeToDocuments', !watcher.agreeToDocuments);
                          trigger('agreeToDocuments');
                        }}
                        className={styles.checkboxText}
                        isValid={!errors?.agreeToDocuments?.message}
                      />
                      <p className={styles.checkboxTextContainer}>
                        <span
                          className={styles.checkboxTextAsLink}
                          onClick={() => openAgreementPopup(AgreementType.AcceptTermsOfUse)}
                        >
                          Consent to Proceed,
                        </span>
                        <span
                          className={styles.checkboxTextAsLink}
                          onClick={() => openAgreementPopup(AgreementType.TranscriptAuthorization)}
                        >
                          Acknowledgement and Consent
                        </span>
                      </p>
                    </div>
                    <p className={styles.errorMessage}>{errors?.agreeToDocuments?.message}</p>
                  </div>
                  <p className={styles.checkingLabel}>
                    I/We understand that by checking the above boxes, I/We have read and agree to the above linked
                    terms. I/We understand that by checking the above boxes I/We certify that all of the information
                    provided in this inquiry is true and correct and confirm that I/We have read and agree to the above
                    linked terms. I/We authorize the company to obtain credit information to process the request I/We
                    have submitted and confirm identity. I/We confirm that all persons who are party to this inquiry
                    participated in the submission of this inquiry.
                  </p>
                </div>
              )}
            />
          </FormBody>
          <div className={styles.navigationButtons}>
            <Button className={styles.backButton} buttonType={ButtonType.Secondary} withArrow onClick={handleBack}>
              Back
            </Button>
            <Button onClick={validateLoanForm} withArrow>
              Continue
            </Button>
          </div>
          <AgreementPopup isModalOpen={isAgreementPopupOpen} onClose={closeAgreementPopup}>
            {getAgreementDocument()}
          </AgreementPopup>
        </>
      )}
    </>
  );
};

export default LoanInformation;
